// Going to a part of a plan's page (today's plans/reveal.ts): a folded phase
// it points to opens, and so does every fold around it, then it is scrolled
// to and given the focus.
import { USER_CONTENT_PREFIX } from './sanitize'

function target(id: string, root?: ParentNode | null): HTMLElement | null {
  if (typeof document === 'undefined' || !id) return null
  const within = root ?? document
  const elements = [...within.querySelectorAll<HTMLElement>('[id]')]
  return elements.find((element) => element.id === id) ?? elements.find((element) => element.id === USER_CONTENT_PREFIX + id) ?? null
}

function reducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Opens the fold `id` names and those around it; false when it is not on the page. */
export function openTarget(id: string, root?: ParentNode | null): boolean {
  const element = target(id, root)
  if (!element) return false
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (node instanceof HTMLDetailsElement && !node.open) node.open = true
  }
  return true
}

/** Opens what `id` names, scrolls to it and gives it the focus; false when it is not on the page. */
export function revealTarget(id: string, smooth = true, root?: ParentNode | null): boolean {
  if (!openTarget(id, root)) return false
  const element = target(id, root)!
  element.scrollIntoView?.({ block: 'start', behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' })
  // Keyboard and screen-reader users continue from the part they chose.
  if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '-1')
  element.focus({ preventScroll: true })
  return true
}

/** What a click on a link inside the plan is for: an anchor's id, or null. */
export function anchorOf(href: string | null): string | null {
  if (!href || !href.startsWith('#') || href.length < 2) return null
  try {
    return decodeURIComponent(href.slice(1))
  } catch {
    return href.slice(1)
  }
}
